import React, { useEffect, useState } from "react";
import { Table, Button, Row, Col, Tooltip, Drawer, Grid } from "antd";
import { DeleteOutlined, EditOutlined, PlusOutlined, QuestionCircleOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchDesignationsList,
  deleteDesignation,
} from "../designationsThunks";
import { App } from "antd";
import { selectDesignationsList } from "../designationsSelectors";
import {
  setSelectedDesignation,
  setIsEditDesignation,
} from "../designationsSlice";
import { getAccessType } from "../../../lib/functions";
import { useIsAdminUser } from "../../../lib/accessList";

const DesignationList = () => {
  const { modal } = App.useApp();
  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isAdminUser = useIsAdminUser();
  const authInfo = useSelector((state) => state.auth?.auth_info);

  const canEdit = authInfo?.can_edit === 1;
  const canDelete = authInfo?.can_delete === 1;


  const designationsList = useSelector(selectDesignationsList);
  let [access_type, access_type_set] = useState("");
  const [helpOpen, setHelpOpen] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });


  useEffect(() => {
    const access = getAccessType("designation_entry");
    access_type_set(access);
  }, []);

  const handleTableChange = (pagination) => {
    setPagination(pagination);
  };

  const handleDelete = async (designation_id, name) => {
    modal.confirm({
      title: "Are you sure?",
      content: `This will delete designation: ${name}`,
      okText: "Yes, Delete",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          await dispatch(deleteDesignation(designation_id)).unwrap();
          modal.success({ title: "Designation deleted successfully" });
          dispatch(fetchDesignationsList());
        } catch (error) {
          modal.warning({ title: error.message || "Delete failed" });
        }
      },
    });
  };

  const columns = [
    {
      title: "NO.",
      render: (_, __, index) => index + 1,
    },
    {
      title: "Designation Name",
      dataIndex: "designation_name",
      sorter: (a, b) => a.designation_name?.localeCompare(b.designation_name),
    },
    ...(access_type === "WRITE"
      ? [
        {
          title: "Actions",
          render: (_, record) => (
            <>
              <Button
                type="link"
                onClick={() => {
                  dispatch(setSelectedDesignation(record));
                  dispatch(setIsEditDesignation(true));
                  navigate("addNewDesignation");
                }}
                icon={<EditOutlined />}
                disabled={!(isAdminUser || canEdit)}
              />
              <Button
                type="link"
                danger
                onClick={() =>
                  handleDelete(record.designation_id, record.designation_name)
                }
                icon={<DeleteOutlined />}
                disabled={!(isAdminUser || canDelete)}
              />
            </>
          ),
        },
      ]
      : []),
  ];

  useEffect(() => {
    dispatch(fetchDesignationsList());
    dispatch(setSelectedDesignation(null));
    dispatch(setIsEditDesignation(false));
  }, [dispatch]);

  return (
    <>
      <Row justify="end" style={{ marginBottom: "32px", marginTop: "32px" }}>
        <Col>
          <Tooltip title="Help">
            <Button
              type="text"
              icon={<QuestionCircleOutlined style={{ fontSize: 18 }} />}
              onClick={() => setHelpOpen(true)}
            />
          </Tooltip>
        </Col>
        <Col>
          {access_type == "WRITE" && (
            <>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => {
                  dispatch(setSelectedDesignation(null));
                  dispatch(setIsEditDesignation(false));
                  navigate("addNewDesignation");
                }}
                data-testid="add-designation-btn"
              >
                Add New
              </Button>
            </>
          )}
        </Col>
      </Row>

      <Table
        columns={columns}
        dataSource={designationsList}
        rowKey="designation_id"
        pagination={{
          ...pagination,
          showSizeChanger: true,
          pageSizeOptions: ["10", "20", "50"],
        }}
        onChange={handleTableChange}
        scroll={{ x: "max-content" }}
      />
      <Drawer
        title="Designation Management Help"
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
        width={420}
        styles={{
          wrapper: {
            paddingTop: !screens.md ? "80px" : undefined
          }
        }}
      >
        <div style={{ lineHeight: 1.8 }}>
          <h2
            style={{
              fontSize: "18px",
              fontWeight: 600,
              marginBottom: "12px",
              marginLeft: "8px",
            }}
          >
            Designation Management
          </h2>

          <p
            style={{
              color: "#555",
              marginBottom: "16px",
              marginLeft: "8px",
            }}
          >
            The Designation module is used to create and manage employee
            designations within the ERP system. Designations help organize
            employee roles, hierarchy, responsibilities, and reporting structures.
          </p>

          <div
            style={{
              background: "#f5f5f5",
              padding: "14px",
              borderRadius: "8px",
              marginBottom: "16px",
            }}
          >
            <strong>Available Actions</strong>

            <ol style={{ paddingLeft: "20px", marginTop: "10px" }}>
              <li>View existing designation records</li>
              <li>Use <strong>Add New</strong> to create designations</li>
              <li>Edit designation details when required</li>
              <li>Delete designations if permitted</li>
              <li>Manage employee role hierarchy</li>
              <li>Organize department and reporting structures</li>
            </ol>
          </div>

          <div
            style={{
              background: "#fafafa",
              border: "1px solid #eee",
              padding: "14px",
              borderRadius: "8px",
            }}
          >
            <strong>Important Notes</strong>

            <ul
              style={{
                paddingLeft: "18px",
                marginTop: "10px",
                color: "#666",
              }}
            >
              <li>Designation names should remain unique.</li>
              <li>Deleting designations may affect linked employee records.</li>
              <li>Access permissions control edit and delete actions.</li>
              <li>Designation structure improves organizational management.</li>
            </ul>
          </div>
        </div>
      </Drawer>
    </>
  );
};

export default DesignationList;
